import React, { Ref, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LaborTimeField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateLaborTime';
import { DiscountType } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/DiscountType';
import { Technicity } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Estimate';
import { MenuProps } from 'antd';
import { Button, InputRef } from 'antd-v5';
import { useTheme } from 'styled-components';
import {
  ChevronRightIcon,
  TechnicityElectricityIcon,
  TechnicityMechanicsIcon,
  TechnicityPaintingIcon,
  TechnicityPanelworkIcon,
  TechnicityTrimIcon,
  TrashAltIcon,
} from 'assets/icons';
import QuantityModule, { QuantityModuleDesign } from 'components/QuantityModule';
import { HIGH, NORMAL, TIER_T1, TIER_T2, TIER_T3, VERY_HIGH } from 'domains/catalog/Catalog.types';
import { getEstimateSettings, updateLaborTime, updateOperationLaborTime } from 'domains/estimate/Estimate.store';
import { ELECTRICITY, MECHANICS, PAINTING, PANELWORK, TRIM } from 'domains/estimate/Estimate.types';
import { getCurrency } from 'domains/user';
import { SDownOutlinedIcon } from 'pages/EstimatePage/TableSection/DownOutlinedIcon/DownOutlinedIcon.styled';
import { theme } from 'styles';
import { Box, Flex, Icon, MarginBox, Text, WithTooltip } from 'UI';
import { getData } from 'utils';
import { textFormatter } from 'utils/format';
import {
  SDiscountInputNumber,
  SDropdown,
  SInput,
  SInputNumber,
  SInputPrice,
  SQuantityModuleFlex,
  SSelect,
  TooltipContent,
} from './TableSection.styled';

export function GetTitle(tr: string, dataCy?: string) {
  return (
    <Text type="base" bold data-cy={dataCy}>
      {tr}
    </Text>
  );
}

export function isEmpty(value: string | number | undefined): boolean {
  return value === undefined || value === '';
}

export interface InputProps {
  saveFunction: (value: string) => void;
}

export interface QuantityModuleProps extends InputProps {
  value: number;
  removeRow: () => void;
  allowDecimal?: boolean;
  precisionMin?: number;
  precisionMax?: number;
  isInvalid?: boolean;
  maxValue?: number;
  isSingleUnitRestricted?: boolean;
  design?: QuantityModuleDesign;
}

export interface TextInputProps extends InputProps {
  value: string | undefined;
  removeRow?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
  minWidth?: number;
  maxLength?: number;
  onBlur?: (v: string) => void;
  passRef?: Ref<InputRef>;
  checkEmpty?: boolean;
  id?: string;
  name?: string;
  autoComplete?: string;
  isInvalid?: boolean;
  dataCy?: string;
}

export interface DiscountInputProps extends InputProps {
  value: string | undefined;
  discountType: DiscountType;
  saveDiscountType: (value: DiscountType) => void;
  isInvalid?: boolean;
}

export interface TechnicityInputProps extends InputProps {
  technicityTypeValue: string | undefined;
  technicityRateValue: Technicity | undefined;
  itemId: string;
  operationId: string | undefined;
}

export interface TextInputWithReadOnly extends InputProps {
  value: string | undefined;
  readOnly?: boolean;
  minWidth?: number;
  isInvalid?: boolean;
}

export interface NumberInputWithReadOnly extends InputProps {
  value: number;
  readOnly?: boolean;
  isInvalid?: boolean;
}

export function TextInput({
  id,
  value,
  name,
  autoComplete,
  saveFunction,
  readOnly,
  removeRow,
  minWidth,
  disabled = false,
  checkEmpty = false,
  isInvalid = false,
  dataCy,
}: Readonly<TextInputProps>) {
  const theme = useTheme();

  return (
    <Flex justify={'center'} align={'center'} minWidth={minWidth}>
      {removeRow && (
        <>
          <Icon IconComponent={TrashAltIcon} onClick={() => removeRow()} size={20} color={theme.color.brand_black} />
          <MarginBox ml={15} />
        </>
      )}
      <WithTooltip title={value} placement={'top'}>
        <TooltipContent data-cy={dataCy}>
          <SInput
            id={id}
            name={name}
            autoComplete={autoComplete}
            initialValue={value}
            disabled={disabled}
            onChangeDebounced={(value) => !readOnly && saveFunction(value)}
            onBlur={(value) => !readOnly && saveFunction(value)}
            bordered={!value || value.length === 0}
            readOnly={readOnly}
            checkEmpty={checkEmpty}
            $isEmpty={isEmpty(value)}
            $isInvalid={isInvalid}
            ellipsis
          />
        </TooltipContent>
      </WithTooltip>
    </Flex>
  );
}

export function QuantityInput({
  value,
  removeRow,
  saveFunction,
  precisionMin,
  precisionMax,
  isInvalid,
  maxValue,
  isSingleUnitRestricted,
  design,
}: Readonly<QuantityModuleProps>) {
  const onChange = useCallback(
    (val: number) => {
      if (val <= 0) {
        removeRow();
      } else {
        saveFunction(String(val));
      }
    },
    [removeRow, saveFunction],
  );

  return (
    <SQuantityModuleFlex justify={'center'} align={'center'} $isInvalid={isInvalid}>
      <QuantityModule
        value={value}
        onChange={onChange}
        showDelete={true}
        precisionMin={precisionMin}
        precisionMax={precisionMax}
        maxValue={maxValue}
        isSingleUnitRestricted={isSingleUnitRestricted}
        size={'small'}
        design={design}
      />
    </SQuantityModuleFlex>
  );
}

export function TechnicityInput({
  technicityTypeValue,
  technicityRateValue,
  itemId,
  saveFunction,
  operationId,
}: Readonly<TechnicityInputProps>) {
  const { t } = useTranslation();
  const estimateSettings = useSelector(getEstimateSettings);
  const settingsData = getData(estimateSettings);
  const dispatch = useDispatch();

  const technicityTypes = [
    { icon: TechnicityMechanicsIcon, title: t('technicity.mechanic', 'Mechanics'), value: MECHANICS },
    { icon: TechnicityPaintingIcon, title: t('technicity.paint', 'Painting'), value: PAINTING },
    { icon: TechnicityPanelworkIcon, title: t('technicity.bodywork', 'Bodywork'), value: PANELWORK },
    { icon: TechnicityElectricityIcon, title: t('technicity.electric', 'Electricity'), value: ELECTRICITY },
    { icon: TechnicityTrimIcon, title: t('technicity.trim', 'Trim'), value: TRIM },
  ];

  const technicityRates = [
    { title: t('technicity.tech_level_1', 'T1 - Basic'), key: NORMAL, value: TIER_T1 },
    { title: t('technicity.tech_level_2', 'T2 - Advanced'), key: HIGH, value: TIER_T2 },
    { title: t('technicity.tech_level_3', 'T3 - Expert Level'), key: VERY_HIGH, value: TIER_T3 },
  ];

  const laborPriceList = settingsData?.laborPriceList || [];

  const [technicity, setTechnicity] = useState<string>();

  useEffect(() => {
    if (technicityTypeValue && technicityRateValue) {
      const typeMatch = technicityTypes.find((type) => type.value === technicityTypeValue.toUpperCase());
      const rateMatch = technicityRates.find((rate) => rate.key === technicityRateValue);
      if (typeMatch && rateMatch) {
        const rateMatchValue = rateMatch.value;
        const initialTechnicity = `${typeMatch.title} ${rateMatchValue}`;
        const initialTechnicityValue = `${typeMatch.value} ${rateMatchValue}`;
        if (technicity !== initialTechnicity) {
          setTechnicity(initialTechnicity);
          saveFunction(initialTechnicityValue);
        }
      }
    }
    // eslint-disable-next-line
  }, [technicityTypeValue, technicityRateValue]);

  const menuItems: MenuProps['items'] = technicityTypes
    .map((techType) => {
      const availableRates = laborPriceList
        .filter((entry) => entry.technicityType === techType.value)
        .map((entry) => entry.technicityLevel);

      if (availableRates.length === 0) {
        return null;
      }

      return {
        key: techType.value,
        label: (
          <Flex justify={'space-between'}>
            <Flex align="center" gap={10}>
              <Icon IconComponent={techType.icon} size={18} />
              <div>{techType.title}</div>
            </Flex>
            <Icon IconComponent={ChevronRightIcon} color={theme.color.grey30} size={12} mr={-12} />
          </Flex>
        ),
        children: technicityRates
          .filter((rate) => availableRates.includes(rate.key))
          .map((rate) => ({
            key: `${techType.value}_${rate.value}`,
            label: rate.title,
          })),
      };
    })
    .filter(Boolean);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const [typeKey, rateValue] = key.split('_');
    const typeMatch = technicityTypes.find((type) => type.value === typeKey);
    const rateMatch = technicityRates.find((rate) => rate.value === rateValue);

    if (typeMatch && rateMatch) {
      const rateMatchValue = rateMatch.value;
      const selectedTechnicity = `${typeMatch.title} ${rateMatchValue}`;
      const selectedTechnicityValue = `${typeMatch.value} ${rateMatchValue}`;
      setTechnicity(selectedTechnicity);
      saveFunction(selectedTechnicityValue);
    }

    const priceMatch = laborPriceList.find(
      (laborPriceEntry) =>
        laborPriceEntry.technicityType === typeMatch?.value && laborPriceEntry.technicityLevel === rateMatch?.key,
    )?.price;

    if (priceMatch) {
      dispatchLaborTimeHourlyRate(dispatch, itemId, operationId, priceMatch);
    }
  };

  const isDisabled = laborPriceList.length <= 0;

  const dropdown = (
    <SDropdown
      destroyPopupOnHide
      menu={{
        expandIcon: null,
        items: menuItems,
        onClick: handleMenuClick,
      }}
      trigger={['hover']}
      placement="bottomLeft"
    >
      <Button disabled={isDisabled}>
        <Flex minWidth={100}>{technicity ?? t('estimate.labor_time.technicity.placeholder', 'Select')}</Flex>
        <SDownOutlinedIcon />
      </Button>
    </SDropdown>
  );

  return (
    <Flex justify="center" minWidth={125}>
      {isDisabled ? (
        <WithTooltip
          title={t('estimate.labor_time.technicity.message', 'Technicity rates must be set up')}
          placement="left"
        >
          <div>{dropdown}</div>
        </WithTooltip>
      ) : (
        dropdown
      )}
    </Flex>
  );
}

export function EstimatePriceInput({
  value,
  saveFunction,
  readOnly,
  minWidth,
  isInvalid,
}: Readonly<TextInputWithReadOnly>) {
  const currency = useSelector(getCurrency);
  return (
    <Flex justify={'center'} align={'center'} minWidth={minWidth ?? 80} data-cy={'unit-price'}>
      <WithTooltip title={value} placement={'top'}>
        <TooltipContent>
          <SInputPrice
            initialValue={value}
            onBlur={(value) => saveFunction(value ?? '')}
            placeholder={'-'}
            currency={currency}
            readOnly={readOnly}
            $isInvalid={isEmpty(value) || isInvalid}
            bordered
          />
        </TooltipContent>
      </WithTooltip>
    </Flex>
  );
}

export function HoursInput({ value, saveFunction, readOnly }: NumberInputWithReadOnly) {
  return (
    <Flex justify={'center'} align={'center'} minWidth={80} data-cy={'time-input'}>
      <WithTooltip title={value} placement={'top'}>
        <TooltipContent>
          <SInputNumber
            initialValue={value}
            onBlur={(value) => saveFunction(value?.toString() ?? '')}
            $isInvalid={isEmpty(value)}
            bordered
            placeholder={'-'}
            addonAfter={'h'}
            min={0}
            precisionMax={1}
            readOnly={readOnly}
          />
        </TooltipContent>
      </WithTooltip>
    </Flex>
  );
}

export function VatInput({ value, saveFunction, readOnly, isInvalid }: NumberInputWithReadOnly) {
  return (
    <Flex justify={'center'} align={'center'} minWidth={80} data-cy={'vat-input'}>
      <WithTooltip title={value && `${value}%`} placement={'top'}>
        <TooltipContent>
          <SInputNumber
            initialValue={value}
            onBlur={(value) => saveFunction(value?.toString() ?? '')}
            $isInvalid={isEmpty(value) || isInvalid}
            bordered
            placeholder={'-'}
            addonAfter={'%'}
            precisionMax={2}
            min={0}
            max={100}
            readOnly={readOnly}
          />
        </TooltipContent>
      </WithTooltip>
    </Flex>
  );
}

export function DiscountInput({ value, saveFunction, discountType, saveDiscountType, isInvalid }: DiscountInputProps) {
  const currency = useSelector(getCurrency);

  const options = [
    { title: '%', value: 'PERCENT' },
    { title: textFormatter.getCurrencySymbol(currency), value: 'VALUE_TO_EXTRACT' },
  ];
  const maxPercent = 100;

  return (
    <Flex justify={'center'} align={'center'} data-cy={'discount-input'}>
      <Flex minWidth={discountType === 'PERCENT' ? 50 : 60}>
        <SDiscountInputNumber
          initialValue={Number(value)}
          onBlur={(value) => saveFunction(value?.toString() ?? '')}
          bordered
          min={0}
          max={discountType === 'PERCENT' ? maxPercent : undefined}
          precisionMax={2}
          $isInvalid={isInvalid}
          data-cy={'discount-input-number'}
        />
      </Flex>
      <Box width={10} />
      <Flex size={0} maxWidth={60} minWidth={60} data-cy={'discount-measurement'}>
        <SSelect
          initialValue={discountType === 'PERCENT' ? 'PERCENT' : 'VALUE_TO_EXTRACT'}
          onChange={(selectValue) => {
            const disType = selectValue === 'PERCENT' ? 'PERCENT' : 'VALUE_TO_EXTRACT';
            saveDiscountType(disType);
          }}
          options={options}
          $isInvalid={isInvalid}
        />
      </Flex>
    </Flex>
  );
}

export function dispatchLaborTimeHourlyRate(
  dispatch: ReturnType<typeof useDispatch>,
  itemId: string,
  operationId: string | undefined,
  priceMatch: string,
) {
  if (operationId) {
    dispatch(
      updateOperationLaborTime({
        itemId,
        operationId,
        newValue: priceMatch,
        field: 'HOURLY_RATE' as LaborTimeField,
      }),
    );
  } else {
    dispatch(
      updateLaborTime({
        itemId,
        newValue: priceMatch,
        field: 'HOURLY_RATE' as LaborTimeField,
      }),
    );
  }
}
